import { Alert, Skeleton } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { useEmailDesign } from "../../../../../email-design/hooks/use-email-design";
import { useAccountId } from "../../../../../generic/hooks/use-account-id";
import { calculateButtonWidthForPoll } from "../../../utils/convert-to-mjml/calculate-button-width-for-poll";
import { calculatePaperSize } from "../../../utils/convert-to-mjml/calculate-paper-size";
import { HorizontalPollOptions } from "./horizontal-poll-options";
import { VerticalPollOptions } from "./vertical-poll-options";

import type { MomentFormMoment } from "../../../../../moment/types/moment-form-moment";
import type { PollWithOptions } from "../../../../../poll/server/supabase-poll-service";
import type { PollSchema } from "../../../plugins/poll-plugin/types";
import type { FC } from "react";

export const CustomPollComponent: FC<{
  poll: PollWithOptions;
  answer_type: Extract<PollSchema["answer_type"], { type: "custom" }>;
}> = ({ poll, answer_type }) => {
  const { control, getValues } = useFormContext<Pick<MomentFormMoment, "email_design_id">>();
  const account_id = useAccountId();

  useWatch({ control, name: ["email_design_id"] });
  const email_design_id = getValues("email_design_id");

  const { loading: emailDesignLoading, data: emailDesign } = useEmailDesign(
    email_design_id ?? undefined,
  );

  if (emailDesignLoading) return <Skeleton variant="rectangular" width="100%" height={100} />;

  if (!emailDesign || !emailDesign.button_settings || !account_id)
    return (
      <Alert severity="error" variant="outlined" sx={{ width: "100%" }}>
        Unable to load poll
      </Alert>
    );

  const paperSize = calculatePaperSize(emailDesign.layout_settings);

  const buttonWidth = calculateButtonWidthForPoll(
    poll.poll.options,
    paperSize ?? 1000,
    emailDesign.button_settings[answer_type.button_type],
  );

  if (answer_type.alignment === "horizontal") {
    return (
      <HorizontalPollOptions
        buttonType={answer_type.button_type}
        options={poll.poll.options}
        buttonWidth={buttonWidth}
        account_id={account_id}
        paperSize={paperSize ?? 1000}
      />
    );
  }

  return (
    <VerticalPollOptions
      buttonType={answer_type.button_type}
      options={poll.poll.options}
      buttonWidth={buttonWidth}
      account_id={account_id}
      paperSize={paperSize ?? 1000}
    />
  );
};
